import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, RefreshCw, ArrowLeft } from 'lucide-react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { showNotification, showError } from '../utils/notificationHandler';
import ContactForm from './ContactForm';
import Button from './shared/Button';
import Card from './shared/Card';
import { VERSION_LABEL, BUILD_HASH, BUILD_TIME } from '../version';

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [authMethod, setAuthMethod] = useState('password');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const captchaRef = useRef(null);
  const history = useHistory();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const checkMagicLinkSent = () => {
      const magicLinkSentTime = localStorage.getItem('magicLinkSentTime');
      if (magicLinkSentTime) {
        const timeDiff = Date.now() - parseInt(magicLinkSentTime);
        if (timeDiff < 5 * 60 * 1000) {
          setMagicLinkSent(true);
          setEmail(localStorage.getItem('magicLinkEmail') || '');
        } else {
          localStorage.removeItem('magicLinkSentTime');
          localStorage.removeItem('magicLinkEmail');
        }
      }
    };

    checkMagicLinkSent();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (forgotPasswordMode) {
        await handleForgotPassword();
        // Reset captcha after password reset
        if (captchaRef.current) {
          captchaRef.current.resetCaptcha();
        }
        setCaptchaToken(null);
      } else if (authMethod === 'password') {
        if (!captchaToken) {
          showNotification('Please complete the captcha', 'error');
          setLoading(false);
          return;
        }
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
          options: {
            captchaToken,
          },
        });
        if (error) throw error;
        handleSuccessfulLogin(data);
        // Reset captcha after successful login
        if (captchaRef.current) {
          captchaRef.current.resetCaptcha();
        }
        setCaptchaToken(null);
      } else if (authMethod === 'magiclink') {
        if (!magicLinkSent) {
          if (!captchaToken) {
            showNotification('Please complete the captcha', 'error');
            setLoading(false);
            return;
          }
          await sendMagicLink();
          // Don't reset captcha after magic link - keep token for potential resends
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      showError(error, 'Login failed. Please check your credentials and try again.');
      // Only reset captcha on error
      if (captchaRef.current) {
        captchaRef.current.resetCaptcha();
      }
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  const sendMagicLink = async () => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          captchaToken,
          redirectTo: 'https://partner.mhsonline.au/auth/callback',
        },
      });

      if (error) {
        console.error('Detailed magic link error:', error);
        throw error;
      }
      setMagicLinkSent(true);
      localStorage.setItem('magicLinkSentTime', Date.now().toString());
      localStorage.setItem('magicLinkEmail', email);
      showNotification('Magic link sent to your email. Please check your inbox and click the link to sign in.', 'success');
    } catch (error) {
      console.error('Caught error in sendMagicLink:', error);
      throw error;
    }
  };

  const handleSuccessfulLogin = async (data) => {
    if (data.user) {
      const { data: accountData, error: accountError } = await supabase
        .from('accounts')
        .select('id, disabled')
        .eq('id', data.user.id)
        .single();

      if (accountError) throw accountError;
      
      // Check if account is disabled
      if (accountData.disabled) {
        showError(
          new Error('Account Disabled'),
          'Your account has been disabled. Please contact an administrator for assistance at matthew@mhs.com.au or call 0433 175 581.'
        );
        
        // Sign the user out immediately
        await supabase.auth.signOut();
        
        // Clear any stored data
        localStorage.removeItem('accountId');
        localStorage.removeItem('magicLinkSentTime');
        localStorage.removeItem('magicLinkEmail');
        
        // Don't proceed to dashboard
        return;
      }
      
      localStorage.setItem('accountId', accountData.id);
      
      localStorage.removeItem('magicLinkSentTime');
      localStorage.removeItem('magicLinkEmail');

      showNotification('Successfully logged in!', 'success');
      history.push('/user/dashboard');
    }
  };

  const handleForgotPassword = async () => {
    if (!captchaToken) {
      showNotification('Please complete the captcha', 'error');
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://partner.mhsonline.au/reset-password',
        captchaToken,
      });

      if (error) throw error;

      showNotification('Password reset instructions sent to your email.', 'success');
      setForgotPasswordMode(false);
    } catch (error) {
      console.error('Forgot password error:', error);
      showError(error, 'Failed to send password reset instructions. Please try again.');
    }
  };

  const toggleForgotPasswordMode = () => {
    setForgotPasswordMode(!forgotPasswordMode);
    setEmail('');
    setCaptchaToken(null);
    if (captchaRef.current) {
      captchaRef.current.resetCaptcha();
    }
  };

  const handleResendMagicLink = async () => {
    setLoading(true);
    
    try {
      // If we have a valid captcha token, try to resend immediately
      if (captchaToken) {
        await sendMagicLink();
        showNotification('Magic link resent! Check your email.', 'success');
      } else {
        // Reset captcha and ask user to complete it again
        setCaptchaToken(null);
        setMagicLinkSent(false);
        if (captchaRef.current) {
          captchaRef.current.resetCaptcha();
        }
        showNotification('Please complete the captcha to resend magic link', 'info');
      }
    } catch (error) {
      console.error('Resend magic link error:', error);
      
      // If token is expired/invalid, reset and ask for new captcha
      if (error.message.includes('captcha') || error.message.includes('already-seen')) {
        setCaptchaToken(null);
        setMagicLinkSent(false);
        if (captchaRef.current) {
          captchaRef.current.resetCaptcha();
        }
        showError(error, 'Captcha expired. Please complete the captcha again to resend magic link.');
      } else {
        showError(error, 'Failed to resend magic link. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const labelClass = 'block text-sm font-medium text-gray-700 mb-1.5';
  const inputClass = 'block w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500';
  const inputIcon = 'absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none';
  const textLink = 'inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900 focus:outline-none focus-visible:underline transition-colors';

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <main className="flex-grow flex items-center justify-center py-10 px-4 sm:px-6">
        <div className="w-full max-w-md space-y-6">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="text-center"
          >
            <img
              src="/Logo2.png"
              alt="MHS Logo"
              className="mx-auto h-20 md:h-24 w-auto"
            />
            <h1 className="mt-4 text-2xl font-semibold text-gray-900">
              Partner Portal
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {forgotPasswordMode ? 'Reset your password' : 'Sign in to your account'}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <Card>
              <form className="space-y-5" onSubmit={handleLogin}>
                <input type="hidden" name="remember" value="true" />
                <div>
                  <label htmlFor="email-address" className={labelClass}>Email address</label>
                  <div className="relative">
                    <Mail className={inputIcon} aria-hidden="true" />
                    <input
                      id="email-address"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className={inputClass}
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={magicLinkSent && !forgotPasswordMode}
                    />
                  </div>
                </div>

                {!forgotPasswordMode && authMethod === 'password' && (
                  <div>
                    <label htmlFor="password" className={labelClass}>Password</label>
                    <div className="relative">
                      <Lock className={inputIcon} aria-hidden="true" />
                      <input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        required
                        className={inputClass}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  {!forgotPasswordMode && (
                    <button
                      type="button"
                      onClick={() => {
                        setAuthMethod(authMethod === 'magiclink' ? 'password' : 'magiclink');
                        setMagicLinkSent(false);
                      }}
                      className={textLink}
                    >
                      {authMethod === 'magiclink' ? 'Use password instead' : 'Use magic link instead'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={toggleForgotPasswordMode}
                    className={textLink}
                  >
                    {forgotPasswordMode ? (
                      <>
                        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                        Back to Login
                      </>
                    ) : (
                      'Forgot your password?'
                    )}
                  </button>
                </div>

                {(!magicLinkSent || authMethod === 'password' || forgotPasswordMode) && (
                  <div className="flex justify-center">
                    <HCaptcha
                      sitekey={window.REACT_APP_HCAPTCHA_SITE_KEY || process.env.REACT_APP_HCAPTCHA_SITE_KEY}
                      onVerify={(token) => setCaptchaToken(token)}
                      ref={captchaRef}
                      size={isMobile ? 'compact' : 'normal'}
                    />
                  </div>
                )}

                {/* Magic Link Sent Message */}
                {authMethod === 'magiclink' && magicLinkSent && !forgotPasswordMode && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Mail className="h-5 w-5 text-green-600 mr-2" aria-hidden="true" />
                      <span className="text-sm font-medium text-green-800">Magic link sent!</span>
                    </div>
                    <p className="text-sm text-green-700 mb-2">
                      Check your email ({email}) and click the link to sign in.
                    </p>
                    <p className="text-xs text-green-600">
                      The link will expire in 5 minutes.
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  loading={loading}
                  disabled={(!magicLinkSent && !captchaToken) || (forgotPasswordMode && !captchaToken) || (authMethod === 'magiclink' && magicLinkSent)}
                >
                  {loading ? 'Processing...' :
                   forgotPasswordMode ? 'Send Reset Instructions' :
                   authMethod === 'password' ? 'Sign in' :
                   magicLinkSent ? 'Magic link sent - Check your email' : 'Send Magic Link'}
                </Button>

                {authMethod === 'magiclink' && magicLinkSent && !forgotPasswordMode && (
                  <div className="flex justify-center">
                    <Button
                      variant="ghost"
                      onClick={handleResendMagicLink}
                      icon={<RefreshCw className="h-4 w-4" aria-hidden="true" />}
                    >
                      Resend Magic Link
                    </Button>
                  </div>
                )}
              </form>
            </Card>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 flex-shrink-0">
        <div className="px-4 py-3">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
            {/* Left: Company Info */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <img src="/Logo2.png" alt="MHS Logo" className="h-6 w-auto mr-2" />
                <span className="font-semibold text-gray-900">MHS</span>
              </div>
              <div className="hidden sm:block h-4 w-px bg-gray-300"></div>
              <p className="text-xs text-gray-600">
                Business Hours: Mon-Fri 7:30 AM - 5:00 PM AWST
              </p>
              <div className="hidden sm:block h-4 w-px bg-gray-300"></div>
              <button
                type="button"
                onClick={() => setShowContactForm(true)}
                className="flex items-center text-xs text-gray-600 hover:text-gray-900 transition-colors duration-200"
              >
                <Mail className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                Contact Us
              </button>
            </div>

            {/* Right: STAUFF Logo and Version */}
            <div className="flex items-center space-x-4">
              <img
                src="/logo-stauff.png"
                alt="STAUFF"
                className="h-6 w-auto opacity-75"
              />
              <div className="hidden sm:block h-4 w-px bg-gray-300"></div>
              <p className="text-xs text-gray-500" title={BUILD_TIME ? `Build ${BUILD_HASH} (${BUILD_TIME})` : BUILD_HASH}>
                System designed with ❤️ by Squid - {VERSION_LABEL}
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Form Modal */}
      <ContactForm
        isOpen={showContactForm}
        onClose={() => setShowContactForm(false)}
        isPublic={true}
      />
    </div>
  );
}
